import { colors } from '@chegg-ui/theme'

export const baseTheme = {
  space: [0, 4, 8, 16, 24, 32],
  breakpoints: ['550px', '768px', '992px', '1200px'],
  spacing: {
    xs: '4px',
    sm: '8px',
    md: '16px',
    md2: '15px',
    md3: '20px',
    lg: '24px',
    xl: '32px',
  },
  borderRadius: '4px',
  border: {
    colors: {
      gray: '#f4f4f4',
    },
  },
  font: {
    heading: {
      size: {
        sm: '18px',
        md: '20px',
        lg: '28px',
        xl: '34px',
      },
    },
    text: {
      size: {
        xs: '9px',
        sm: '12px',
        md: '14px',
        lg: '16px',
        xl: '18px',
        xlg: '24px',
      },
    },
    lineHeight: {
      md: 1.2,
      lg: 1.5,
    },
    weight: {
      light: '300',
      regular: 'normal',
      medium: '500',
      bold: 'bold',
      extraBold: '800',
    },
  },
  colors: {
    red: '#d33f2a',
    black: '#000',
    white: colors.white,
    orange: '#ff5313',
    orangeLight: '#FFF2EC',
    orangeDark: '#ec4203',
    orangeDarker: '#d23900',
    beige: '#ffe7c4',
    sand: '#ffb751',
    yellow: '#ffc800',
    yellowLight: 'rgba(255,200,0,0.25)',
    blue: colors.royal,
    greyEEE: '#eee',
    greyE5: '#e5e5e5',
    greyDDD: '#ddd',
    grey76: colors.grey,
    grey: colors.greyMedium,
    greyLight: colors.greyMedlight,
    greylighter: '#d6d6d6',
    greyLightest: '#f8f8f8',
    greyDark: '#333',
    gold: '#dd8d00',
    green: '#4dc66d',
    greenLight: '#d9f3e0',
    teal: '#2caea6',
    altTeal: '#00838f',
    borderColor: '#ededed',
    borderColor2: '#ddd',
  },
}

export const mainTheme = {
  ...baseTheme,
  site: 'main',
}

export const adFrameTheme = {
  ...mainTheme,
  site: 'main-adframe',

  /**
   * breakpoints
   *
   * These values are the screenwidths at which we are guaranteed that
   * the content area is never going to drop below the values specified
   * in our old breakpoints. See `contentFrameWidths` and `sideFrameSizes
   * and adLayoutBreakpoints` for more information.
   */
  breakpoints: ['850px', '1068px', '1392px', '1600px'],

  /**
   * contentFrameWidths
   *
   * Each entry of the array is the amount of width available at the
   * corresponding screen size defined in `breakpoints`. They demarcate
   * when a different design variant should be visible. The values were
   * chosen to reduce rubber-banding between "design variants", though
   * admittedly, the change from having no right frame to having one is
   * rather jarring, but as of yet, there's no good way to avoid it other
   * than keeping the "mobile" design active until 850px.
   *
   * The values are based on the old screen breakpoints that we
   * used in our codebase.
   *
   * NOTE: Our breakpoints used to be based on the values from
   * our bootstrap config, which were 480px, 768px, 992px and 1200px.
   * At some point we changed one of the breakpoints to 550px, and we
   * used this value in certain places where 768px used to be, we will
   * need to review these at some point.
   */
  contentFrameWidths: ['550px', '768px', '992px', '1200px'],

  /**
   * sideFrameSizes and adLayoutBreakpoints
   *
   * These values represent the points at which the layout of the screen
   * changes to accomodate layout of the ads
   *
   * [0, 619px] -> No right frame
   * [620px, 1199px] -> 300px right frame
   * [1200px, 1279px] -> 400px right frame
   * [1280px, +Infinity] -> 482px right frame
   *
   * The values for contentFrameWidths are tied to the adLayoutBreakpoints
   * and the size of the sideFrame
   */
  sideFrameSizes: ['300px', '400px', '482px'],
  adLayoutBreakpoints: ['620px', '1200px', '1280px'],
}

export const adjust = (unitfulValue: string, adjustment: number) => {
  return unitfulValue.replace(/^(-?\d+)(.*)$/, (...match) => {
    const [, value, unit] = match

    return `${Number.parseInt(value, 10) + adjustment}${unit}`
  })
}

export const themeBreakPoints = {
  extraSmall: (theme) => theme.breakpoints[0],
  small: (theme) => theme.breakpoints[1],
  medium: (theme) => theme.breakpoints[2],
  large: (theme) => theme.breakpoints[3],
}

// helpers to get the ad layout breakpoints from the theme
export const smallLayout = (theme) => theme.adLayoutBreakpoints[0]
export const mediumLayout = (theme) => theme.adLayoutBreakpoints[1]
export const largeLayout = (theme) => theme.adLayoutBreakpoints[2]
